import {
  makeStyles,
  SearchBox,
  SearchBoxChangeEvent,
  ToggleButton,
} from "@fluentui/react-components";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FlightListContext } from "../../../context/FlightListContext";
import { LayoutContext } from "../../../context/LayoutContext";
import { $cloud20, $primary, $white } from "../../../cssjs/colors";
import { BarsIcon } from "../icons/bars/BarsIcon";
import AmdTagPicker from "../tag-picker/TagPicker";

interface IAirportListProps {
  isMapView?: boolean;
  sideBarActive: boolean;
  setSideBarActive: any;
  searchInputHandler: (event: SearchBoxChangeEvent) => void;
  children?: React.ReactElement;
  overlay?: React.ReactElement;
  overlayActive?: boolean;
}

const extractIds = (data: any[]): string[] => {
  return data.map((flight) => flight.flightNumber);
};

const getMultiAirportStyle = (
  isMultiAirportPage: boolean,
  sideBarActive: boolean,
  openStyle: string,
  closedStyle: string
): string | undefined => {
  if (!isMultiAirportPage) {
    return undefined;
  }

  return sideBarActive ? openStyle : closedStyle;
};

const Sidebar: React.FunctionComponent<IAirportListProps> = ({
  sideBarActive,
  setSideBarActive,
  searchInputHandler,
  children,
  overlay,
  overlayActive = false,
}) => {
  const classes = useStyles();
  const burgerMenuHandler = React.useCallback(() => {
    setSideBarActive((prev: boolean) => !prev);
  }, [setSideBarActive]);

  const location = useLocation();
  const isMultiAirportPage = location.pathname.startsWith("/multi-airports");
  const showTagPicker = !isMultiAirportPage;
  const multiAirportStyles = getMultiAirportStyle(
    isMultiAirportPage,
    sideBarActive,
    classes.multiAirportContainerOpen,
    classes.multiAirportContainerClosed
  );

  const placeholderText = isMultiAirportPage
    ? "Search airport"
    : "Search flight";

  const [selectedOptions, setSelectedOptions] = React.useState<string[]>([]);
  const [flightList, setFlightList] = React.useState<any[]>([]);

  const updateFlightList = useCallback((flightListData: any[]) => {
    setFlightList(flightListData);
  }, []);

  const options = extractIds(flightList);

  const contextValue = useMemo(
    () => ({ selectedOptions, updateFlightList }),
    [selectedOptions]
  );
  const { setFlightFilterParams } = useContext(LayoutContext);

  useEffect(() => {
    setFlightFilterParams((oldParams) => {
      const updatedParams = { ...oldParams };
      updatedParams.flightIDs = [...selectedOptions];
      return updatedParams;
    });
  }, [selectedOptions]);

  return (
    <section
      data-testid="sidebar-container"
      className={`${classes.container} ${
        sideBarActive ? classes.containerOpen : undefined
      } ${multiAirportStyles}`}
    >
      <div
        className={`${classes.mainLayer} ${
          sideBarActive && overlayActive ? classes.mainLayerHidden : undefined
        }`}
        aria-hidden={sideBarActive && overlayActive ? true : undefined}
      >
        <header
          className={`mapview-sidebar ${classes.header} ${
            sideBarActive ? classes.headerOpen : classes.headerClosed
          } ${
            isMultiAirportPage && !sideBarActive
              ? classes.multiAirportHeaderClosed
              : undefined
          }`}
        >
          <ToggleButton
            onClick={burgerMenuHandler}
            className={`${classes.burgerMenu}`}
            icon={<BarsIcon />}
            aria-label="Open the menu"
          />
          {showTagPicker ? (
            <div className={classes.tagPicker}>
              <AmdTagPicker
                setSelectedOptions={setSelectedOptions}
                selectedOptions={selectedOptions}
                options={options}
                label="Flights"
              />
            </div>
          ) : (
            <SearchBox
              placeholder={placeholderText}
              onChange={(event: SearchBoxChangeEvent) => {
                searchInputHandler(event);
              }}
              size="medium"
              appearance="outline"
              aria-label="search"
              className={classes.sidebar}
            />
          )}
        </header>
        <FlightListContext.Provider value={contextValue}>
          {sideBarActive ? children : null}
        </FlightListContext.Provider>
      </div>
      {sideBarActive && overlayActive ? (
        <div className={classes.overlayLayer} data-testid="overlay-layer">
          {overlay}
        </div>
      ) : null}
    </section>
  );
};

export default Sidebar;

const useStyles = makeStyles({
  container: {
    position: "relative",
    display: "grid",
    zIndex: 3,
    maxWidth: "400px",
    borderRadius: "8px",
    marginLeft: "8px",
    marginBottom: "16px",
  },
  mainLayer: {
    gridArea: "1 / 1",
    minWidth: 0,
    minHeight: 0,
  },
  mainLayerHidden: {
    visibility: "hidden",
    pointerEvents: "none",
  },
  overlayLayer: {
    gridArea: "1 / 1",
    zIndex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    background: $white,
    borderRadius: "8px",
  },
  containerOpen: {
    background: $cloud20,
    marginLeft: "16px",
  },
  multiAirportContainerOpen: {
    marginTop: "-32px",
  },
  multiAirportContainerClosed: {
    marginTop: "0px",
  },
  header: {
    borderTopRightRadius: "8px",
    borderTopLeftRadius: "8px",
    display: "flex",
    justifyContent: "flex-start",
    color: $white,
  },
  headerOpen: {
    background: $white,
    padding: "16px",
  },
  headerClosed: {
    padding: "0px 8px 16px",
  },
  multiAirportHeaderClosed: {
    padding: "8px 8px 16px",
  },
  sidebar: {
    marginTop: "0",
    minWidth: "280px",
    marginRight: "4px",
    width: "100%",
  },
  burgerMenu: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${$primary}`,
    padding: "12px",
    minWidth: "42px",
    maxHeight: "36px",
    background: $white,
    color: $primary,
    "&:hover": {
      border: `1px solid ${$primary}`,
      background: $primary,
      color: $white,
      fill: $white,
    },
    marginRight: "16px",
  },
  tagPicker: {
    width: "100%",
    "& .fui-Tag__media": {
      paddingRight: "0px",
    },
  },
});
